import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ClientBadges } from '@/components/Inventory/UserSecurityPostureTab/ClientBadges';
import { AgentVersionWithUpdate } from '@/components/shared/AgentVersionWithUpdate';
import { useLatestReleases } from '@/hooks/api/useLatestReleases';
import {
  groupClientsByCoverage,
  sortClientsMismatchedFirst,
} from '@/components/Inventory/UserSecurityPostureTab/client-utils';
import type { UserSecurityPostureDataTableItem } from '@kirin/api-client/contracts/user-security-posture';
import { ORGANIZATION_ALERTS_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/alerts';
import { FilterOperators } from '@kirin/api-client/contracts/table';
import {
  buildAlertsDeepLinkUrl,
  createFilter,
} from '@/pages/organization/tabs/DataTableAlertsTab/utils';
import { ALERT_LOOKBACK_DAYS } from './constants';
import type { TimestampFormat } from '@/contexts/TimestampFormatContext';
import { withTruncation } from '@/components/shared/hocs/withTruncation';
import type { TruncationInjectedProps } from '@/components/shared/hocs/withTruncation';

interface TruncatedTextCellProps extends TruncationInjectedProps {
  text: string;
  className?: string;
}

/**
 * Cell text that ellipsis-truncates to its column width and reveals the full
 * value in a tooltip only when actually clipped (KIR-2475 DoD #5).
 */
const TruncatedTextCell = withTruncation<TruncatedTextCellProps>(
  ({ text, className, truncationRef, isTruncated, checkTruncation }) => {
    const content = (
      <span
        ref={truncationRef}
        className={cn('block max-w-full truncate', className)}
        onMouseEnter={checkTruncation}
        onFocus={checkTruncation}
      >
        {text}
      </span>
    );

    if (!isTruncated) {
      return content;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent>{text}</TooltipContent>
      </Tooltip>
    );
  }
);

export function NameCell({
  row,
}: {
  row: UserSecurityPostureDataTableItem;
}): ReactElement {
  return <TruncatedTextCell text={row.name || '—'} className="font-medium" />;
}

export function EmailCell({
  row,
}: {
  row: UserSecurityPostureDataTableItem;
}): ReactElement {
  return (
    <TruncatedTextCell
      text={row.email || '—'}
      className="text-muted-foreground"
    />
  );
}

interface IDEsCoveredCellProps {
  row: UserSecurityPostureDataTableItem;
  onViewDetails: () => void;
}

export function IDEsCoveredCell({
  row,
  onViewDetails,
}: IDEsCoveredCellProps): ReactElement {
  const grouped = useMemo(
    () => groupClientsByCoverage(row.clients ?? []),
    [row.clients]
  );
  // Surface personal/corporate (red) accounts first so they are never the ones
  // hidden behind the "+N more" truncation (KIR-2916).
  const coveredClients = useMemo(
    () => sortClientsMismatchedFirst(grouped.covered),
    [grouped.covered]
  );
  const showInfoButton = grouped.covered.length > 0;

  const infoButton = showInfoButton ? (
    <button
      type="button"
      onClick={e => {
        e.stopPropagation();
        onViewDetails();
      }}
      className="p-1 rounded hover:bg-muted transition-colors shrink-0 cursor-pointer"
      aria-label="View client details"
    >
      <Info className="h-4 w-4 text-muted-foreground" />
    </button>
  ) : undefined;

  // No px cap here: the column is greedy (width:100%) so ClientBadges measures
  // the full cell width and renders inline, only collapsing to "+N" when the
  // badges genuinely overflow the available space (KIR-3541).
  return (
    <ClientBadges
      clients={coveredClients}
      leading={infoButton}
      emptyClassName="coverage-dash-indent"
      onOverflowClick={onViewDetails}
    />
  );
}

interface IDEsSupportedCellProps {
  row: UserSecurityPostureDataTableItem;
  onViewDetails: () => void;
}

export function IDEsSupportedCell({
  row,
  onViewDetails,
}: IDEsSupportedCellProps): ReactElement {
  const grouped = useMemo(
    () => groupClientsByCoverage(row.clients ?? []),
    [row.clients]
  );
  // Surface personal/corporate (red) accounts first so they are never the ones
  // hidden behind the "+N more" truncation (KIR-2916).
  const supportedClients = useMemo(
    () => sortClientsMismatchedFirst(grouped.supported),
    [grouped.supported]
  );
  const showInfoButton =
    grouped.supported.length > 0 && grouped.covered.length === 0;

  const infoButton = showInfoButton ? (
    <button
      type="button"
      onClick={e => {
        e.stopPropagation();
        onViewDetails();
      }}
      className="p-1 rounded hover:bg-muted transition-colors shrink-0 cursor-pointer"
      aria-label="View client details"
    >
      <Info className="h-4 w-4 text-muted-foreground" />
    </button>
  ) : undefined;

  // No px cap here: like the Covered cell, this column is greedy (width:100%) so
  // ClientBadges renders inline against the full cell width and only collapses
  // to "+N" on true overflow (KIR-3541).
  return (
    <ClientBadges
      clients={supportedClients}
      leading={infoButton}
      onOverflowClick={onViewDetails}
    />
  );
}

export function MCPCountCell({
  row,
}: {
  row: UserSecurityPostureDataTableItem;
}): ReactElement {
  return <div className="text-center-cell">{row.mcpCount}</div>;
}

function getAlertCountStyles(count: number): string {
  if (count === 0) return '';
  if (count <= 5)
    return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  if (count <= 20)
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  return 'bg-red-200 text-red-900 font-bold dark:bg-red-900/60 dark:text-red-100';
}

interface AlertsCellProps {
  row: UserSecurityPostureDataTableItem;
  organizationId: string;
}

/**
 * Alert count cell for the Users DataTable.
 * Builds a deep link to the Alerts table filtered by this user + last N days,
 * using the alerts DataTable constants — no hardcoded paths or param names.
 */
export function AlertsCell({
  row,
  organizationId,
}: AlertsCellProps): ReactElement {
  const [now] = useState(Date.now);
  const href = useMemo(() => {
    const sevenDaysAgo = new Date(
      now - ALERT_LOOKBACK_DAYS * 24 * 60 * 60 * 1000
    ).toISOString();

    return (
      buildAlertsDeepLinkUrl(
        organizationId,
        [
          createFilter(ORGANIZATION_ALERTS_FILTERABLE_FIELDS.USERS, [
            row.userId,
          ]),
          createFilter(
            ORGANIZATION_ALERTS_FILTERABLE_FIELDS.LAST_SEEN_AT,
            [sevenDaysAgo],
            FilterOperators.GREATER_THAN
          ),
        ],
        // KIR-2606 #3: count reflects ACTIVE alerts only — override the
        // saved preference for this navigation. `useAlertVisibility`
        // honors and strips the param once.
        { visibility: 'active' }
      ) ?? `/${organizationId}/alerts`
    );
  }, [organizationId, row.userId, now]);

  const severityStyles = getAlertCountStyles(row.alertCount);
  const hasStyles = severityStyles.length > 0;

  return (
    <div className="text-center-cell">
      <Link
        to={href}
        onClick={e => e.stopPropagation()}
        className={cn(
          'inline-flex items-center justify-center font-medium transition-colors cursor-pointer underline underline-offset-2',
          hasStyles
            ? 'decoration-current hover:decoration-current'
            : 'text-primary decoration-primary/40 hover:decoration-primary',
          hasStyles ? 'px-2 py-0.5 rounded-md' : '',
          severityStyles
        )}
      >
        {row.alertCount}
      </Link>
    </div>
  );
}

/**
 * KIR-3035 / KIR-3911: endpoints count cell for the Users DataTable. Clicking
 * opens the per-user endpoints dialog. The WHOLE cell is clickable (not just the
 * number) — KIR-3911. Zero endpoints render "N/A" (muted, non-clickable) rather
 * than 0.
 */
export function EndpointsCell({
  row,
  onView,
}: {
  row: UserSecurityPostureDataTableItem;
  onView: (row: UserSecurityPostureDataTableItem) => void;
}): ReactElement {
  const count = row.endpointCount ?? 0;

  if (count === 0) {
    return (
      <div className="text-center-cell">
        <span className="text-muted-foreground">N/A</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-label="View endpoints"
      onClick={e => {
        e.stopPropagation();
        onView(row);
      }}
      className="flex h-full w-full items-center justify-center font-medium text-primary underline underline-offset-2 decoration-primary/40 hover:decoration-primary transition-colors cursor-pointer"
    >
      {count}
    </button>
  );
}

/**
 * KIR-3911 / KIR-2962: agent-version cell for the Users DataTable. Shows the
 * agent version of the user's least up-to-date live endpoint (server-computed
 * `leastAgentVersion`) via {@link AgentVersionWithUpdate}. "Outdated" (red) +
 * the amber "Update available" pill are judged against the REAL latest release
 * ({@link useLatestReleases} → GitHub pipeline), not the dashboard build
 * version. "N/A" when the version is unknown.
 */
export function AgentVersionCell({
  row,
}: {
  row: UserSecurityPostureDataTableItem;
}): ReactElement {
  const { data: latestReleases } = useLatestReleases();
  const agentLatest = latestReleases?.agent.latestVersion ?? null;
  return (
    <div className="text-center-cell">
      <AgentVersionWithUpdate
        version={row.leastAgentVersion ?? null}
        latestVersion={agentLatest}
      />
    </div>
  );
}

export function LastActiveCell({
  row,
  format,
}: {
  row: UserSecurityPostureDataTableItem;
  format: TimestampFormat;
}): ReactElement {
  const wrapperClass = format === 'absolute' ? undefined : 'flex-center-cell';

  return (
    <div className={wrapperClass}>
      <span className="text-muted-foreground">
        {/* KIR-3415: "Kirin Last Active" data is not accurate to the minute, so
          use the fuzzy variant — never show an exact minute count below the
          hour ("less than 15 minutes ago" / "less than 1 hour ago"). */}
        {row.lastActiveAt ? (
          <RelativeTimeWithTooltip
            showIcon={false}
            date={row.lastActiveAt}
            format={format}
            variant="fuzzy"
          />
        ) : (
          '—'
        )}
      </span>
    </div>
  );
}
